import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  SettingsSubRail,
  getVisibleSettingsSubPages,
  DEFAULT_SETTINGS_SUB_PAGE,
} from '@/components/settings/settings-sub-rail';
import { PreferencesSubPage } from '@/components/settings/sub-pages/preferences-sub-page';
import { ThreadGroupsSubPage } from '@/components/settings/sub-pages/thread-groups-sub-page';
import { AppointmentConfirmationSubPage } from '@/components/settings/sub-pages/appointment-confirmation-sub-page';
import { MessageTemplatesSubPage } from '@/components/settings/sub-pages/message-templates-sub-page';
import { QueuesSubPage } from '@/components/settings/sub-pages/queues-sub-page';
import { WorkRolesSubPage } from '@/components/settings/sub-pages/work-roles-sub-page';
import { WorkPlanSubPage } from '@/components/settings/sub-pages/work-plan-sub-page';
import { CalendarsSubPage } from '@/components/settings/sub-pages/calendars-sub-page';
import { ClinicVisibilitySubPage } from '@/components/settings/sub-pages/clinic-visibility-sub-page';
import { TeamEmployeesSubPage } from '@/components/settings/sub-pages/team-employees-sub-page';
import { TeamGroupsSubPage } from '@/components/settings/sub-pages/team-groups-sub-page';
import { WhatsappConnectionsSubPage } from '@/components/settings/sub-pages/whatsapp-connections-sub-page';
import { BotServiceHoursSubPage } from '@/components/settings/sub-pages/bot-service-hours-sub-page';
import { FaceUnlockSubPage } from '@/components/settings/sub-pages/face-unlock-sub-page';
import { fetchMessagesSettings, putMessagesSettings } from '@/lib/api/settings';
import { hydrateThreadGroupsFromServer } from '@/lib/stores/thread-groups-store';
import { usePermissions } from '@/hooks/use-permissions';
import { hasPermission } from '@/lib/stores/permissions-store';
import { isWebMode } from '@/lib/config/web-mode';
import {
  BOT_ACK_DEFAULT_MAX_HOURS,
  BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
  PATIENT_REPLY_DEFAULT_MAX_HOURS,
  PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
} from '@/lib/messages/messages-helpers';

// Settings is a list+detail: a left sub-rail (SHARED / PATIENT QUEUE / TEAM) switches the
// active sub-page rendered on the right. This page owns the tenant-wide shared Messages
// settings (bot-ack thresholds + the canEdit gate, resolved from one fetch and threaded into
// the Preferences and Thread groups sub-pages) so the editors share one source of truth.
function SettingsPage({ theme, onSelectTheme }) {
  // Land on the first section this user can actually see: a group may hide Preferences (or
  // lack rights to it) while leaving another section visible.
  const [activeSubPage, setActiveSubPage] = useState(
    () => getVisibleSettingsSubPages()[0]?.id || DEFAULT_SETTINGS_SUB_PAGE,
  );
  const [botAck, setBotAck] = useState({
    thresholdMinutes: BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
    maxHours: BOT_ACK_DEFAULT_MAX_HOURS,
  });
  // Separate from botAck: the two sections share a threshold but not a ceiling, so that a patient
  // nobody answered stays in "Needs you" long after a stale bot reply has aged out.
  const [patientReply, setPatientReply] = useState({
    thresholdMinutes: PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
    maxHours: PATIENT_REPLY_DEFAULT_MAX_HOURS,
  });
  // RBAC drives the section gates; the shared-settings canEdit is confirmed by the server
  // response below (it computes the same employee + settings:manage answer).
  const permissionsState = usePermissions();
  const [canEditShared, setCanEditShared] = useState(() => hasPermission('settings', 'manage'));

  // Snap off a section that stopped being visible — rights revoked mid-session, or a group's
  // menu-visibility changing under an open page. Mirrors the forbidden-page redirect in App.jsx.
  useEffect(() => {
    const visible = getVisibleSettingsSubPages();
    if (visible.some((p) => p.id === activeSubPage)) return;
    setActiveSubPage(visible[0]?.id || DEFAULT_SETTINGS_SUB_PAGE);
  }, [activeSubPage, permissionsState]);

  useEffect(() => {
    let cancelled = false;
    fetchMessagesSettings()
      .then((res) => {
        if (cancelled) return;
        hydrateThreadGroupsFromServer(res.threadGroups);
        setCanEditShared(Boolean(res.canEdit));
        setBotAck((prev) => ({
          thresholdMinutes: Number.isFinite(res.botAck?.thresholdMinutes)
            ? res.botAck.thresholdMinutes
            : prev.thresholdMinutes,
          maxHours: Number.isFinite(res.botAck?.maxHours) ? res.botAck.maxHours : prev.maxHours,
        }));
        setPatientReply((prev) => ({
          thresholdMinutes: Number.isFinite(res.patientReply?.thresholdMinutes)
            ? res.patientReply.thresholdMinutes
            : prev.thresholdMinutes,
          maxHours: Number.isFinite(res.patientReply?.maxHours)
            ? res.patientReply.maxHours
            : prev.maxHours,
        }));
      })
      .catch(() => {
        /* keep warm-start values; the editors still render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const commitBotAck = async (patch) => {
    const next = { ...botAck, ...patch };
    try {
      const res = await putMessagesSettings({ botAck: next });
      setBotAck((prev) => ({
        thresholdMinutes: Number.isFinite(res.botAck?.thresholdMinutes)
          ? res.botAck.thresholdMinutes
          : prev.thresholdMinutes,
        maxHours: Number.isFinite(res.botAck?.maxHours) ? res.botAck.maxHours : prev.maxHours,
      }));
      toast.success('Shared Messages settings saved for everyone.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save shared settings';
      toast.error(message);
      // Re-sync the inputs to the last known values (key={value} remounts them).
      setBotAck((prev) => ({ ...prev }));
    }
  };

  const commitPatientReply = async (patch) => {
    const next = { ...patientReply, ...patch };
    try {
      const res = await putMessagesSettings({ patientReply: next });
      setPatientReply((prev) => ({
        thresholdMinutes: Number.isFinite(res.patientReply?.thresholdMinutes)
          ? res.patientReply.thresholdMinutes
          : prev.thresholdMinutes,
        maxHours: Number.isFinite(res.patientReply?.maxHours)
          ? res.patientReply.maxHours
          : prev.maxHours,
      }));
      toast.success('Shared Messages settings saved for everyone.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save shared settings';
      toast.error(message);
      setPatientReply((prev) => ({ ...prev }));
    }
  };

  const renderSubPage = () => {
    const preferences = (
      <PreferencesSubPage
        theme={theme}
        onSelectTheme={onSelectTheme}
        canEditShared={canEditShared}
        botAck={botAck}
        onCommitBotAck={commitBotAck}
        patientReply={patientReply}
        onCommitPatientReply={commitPatientReply}
      />
    );
    // Permission-gated sections fall back to Preferences: the rail hides their entries, so
    // this only fires when rights are revoked while the section is open (or on stale state).
    // Deliberately RBAC-only — a section HIDDEN by a menu key still renders if somehow reached.
    // Menu keys trim visibility and must never become an enforcement gate; the snap effect
    // above makes a hidden section practically unreachable without breaking that contract.
    switch (activeSubPage) {
      case 'thread-groups':
        return <ThreadGroupsSubPage canEditShared={canEditShared} />;
      case 'appointment-confirmation':
        return <AppointmentConfirmationSubPage />;
      case 'message-templates':
        return <MessageTemplatesSubPage />;
      case 'clinic-visibility':
        // SHARED, no RBAC gate — the editor takes canEdit from the server payload.
        return <ClinicVisibilitySubPage />;
      case 'face-unlock':
        if (isWebMode()) return preferences;
        return <FaceUnlockSubPage />;
      case 'work-roles':
        if (!hasPermission('scheduling', 'view')) return preferences;
        return <WorkRolesSubPage canEdit={hasPermission('scheduling', 'manage')} />;
      case 'work-plan':
        if (!hasPermission('scheduling', 'view')) return preferences;
        return <WorkPlanSubPage canEdit={hasPermission('scheduling', 'manage')} />;
      case 'calendars':
        if (!hasPermission('scheduling', 'view')) return preferences;
        return <CalendarsSubPage canEdit={hasPermission('scheduling', 'manage')} />;
      case 'queues':
        if (!hasPermission('queue_config', 'view')) return preferences;
        return <QueuesSubPage canEdit={hasPermission('queue_config', 'manage')} />;
      case 'team-employees':
        if (!hasPermission('staff', 'manage')) return preferences;
        return <TeamEmployeesSubPage />;
      case 'team-groups':
        if (!hasPermission('staff', 'manage')) return preferences;
        return <TeamGroupsSubPage />;
      case 'bot-service-hours':
        if (!hasPermission('settings', 'view')) return preferences;
        // canEdit comes from the payload (the server's settings:manage answer), like the
        // other shared editors.
        return <BotServiceHoursSubPage />;
      case 'whatsapp-connections':
        if (!hasPermission('integrations', 'view')) return preferences;
        // manage covers both the QR (a live credential — scanning it binds a phone to the
        // tenant's number) and the re-bind itself.
        return <WhatsappConnectionsSubPage canEdit={hasPermission('integrations', 'manage')} />;
      case 'preferences':
      default:
        return preferences;
    }
  };

  return (
    <div className="flex h-full min-h-0">
      <SettingsSubRail active={activeSubPage} onSelect={setActiveSubPage} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{renderSubPage()}</div>
    </div>
  );
}

export default SettingsPage;
